import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import {
  ChevronRight,
  Loader2,
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  Send,
  User,
  Headphones,
  Bot,
  Paperclip,
  Sparkles,
} from 'lucide-react';
import { AIResponseCard } from '@/components/AIResponseCard';
import { ticketService } from '@/services/ticket.service';
import { useAuthStore } from '@/stores/authStore';
import { formatDistanceToNow, format } from 'date-fns';
// H-1: imported by SUBPATH, not from the '@centraoneplus/ui' barrel.
// packages/ui is a source-only library, so importing its index pulls every
// component into this app's type program - and under `noUnusedLocals` that
// surfaced 43 latent errors in files this app never uses. apps/web already
// imports the barrel so it is unaffected; customer-portal did not.
import { sanitizeMessageHtml } from '@centraoneplus/ui/src/security/sanitize-html';

const statusConfig: Record<string, { icon: typeof Clock; color: string; label: string }> = {
  new: { icon: Clock, color: 'text-blue-600 bg-blue-100', label: 'New' },
  open: { icon: AlertCircle, color: 'text-yellow-600 bg-yellow-100', label: 'Open' },
  pending: { icon: Clock, color: 'text-orange-600 bg-orange-100', label: 'Pending' },
  resolved: { icon: CheckCircle, color: 'text-green-600 bg-green-100', label: 'Resolved' },
  closed: { icon: XCircle, color: 'text-gray-600 bg-gray-100 dark:bg-gray-800 dark:text-gray-400', label: 'Closed' },
};

const priorityConfig: Record<string, { color: string; label: string }> = {
  low: { color: 'text-gray-600 bg-gray-100 dark:bg-gray-800 dark:text-gray-400', label: 'Low' },
  medium: { color: 'text-blue-600 bg-blue-100', label: 'Medium' },
  high: { color: 'text-orange-600 bg-orange-100', label: 'High' },
  urgent: { color: 'text-red-600 bg-red-100', label: 'Urgent' },
};

interface ReplyFormData {
  content: string;
}

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuthStore();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ReplyFormData>();

  const { data: ticket, isLoading } = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => ticketService.getTicket(id!),
    enabled: !!id && isAuthenticated,
  });

  const { data: messages, isLoading: messagesLoading } = useQuery({
    queryKey: ['ticket-messages', id],
    queryFn: () => ticketService.getTicketMessages(id!),
    enabled: !!id && isAuthenticated,
  });

  const replyMutation = useMutation({
    mutationFn: (data: ReplyFormData) =>
      ticketService.replyToTicket(id!, data.content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-messages', id] });
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      reset();
    },
  });

  const onSubmit = (data: ReplyFormData) => {
    replyMutation.mutate(data);
  };

  if (!isAuthenticated) {
    return (
      <div className="py-16">
        <div className="container max-w-2xl">
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-yellow-500" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Sign in Required</h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Please sign in to view ticket details.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              Sign In
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="container py-16 text-center">
        <AlertCircle className="h-16 w-16 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Ticket Not Found</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">The ticket you're looking for doesn't exist.</p>
        <Link
          to="/my-tickets"
          className="text-primary-600 hover:text-primary-700 font-medium"
        >
          Back to My Tickets
        </Link>
      </div>
    );
  }

  const status = statusConfig[ticket.status] || statusConfig.new;
  const StatusIcon = status.icon;
  const priority = priorityConfig[ticket.priority] || priorityConfig.medium;
  const isTicketOpen = ticket.status !== 'resolved' && ticket.status !== 'closed';

  return (
    <div className="py-8">
      <div className="container">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-6">
          <Link to="/" className="hover:text-primary-600">
            Home
          </Link>
          <ChevronRight className="h-4 w-4" />
          <Link to="/my-tickets" className="hover:text-primary-600">
            My Tickets
          </Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-gray-900 dark:text-white font-medium">#{ticket.ticketNumber}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Ticket Header */}
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-sm font-mono text-gray-500 dark:text-gray-400">
                      #{ticket.ticketNumber}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status.color}`}
                    >
                      <StatusIcon className="h-3 w-3" />
                      {status.label}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${priority.color}`}
                    >
                      {priority.label}
                    </span>
                  </div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{ticket.subject}</h1>
                </div>
              </div>

              <div className="text-sm text-gray-500 dark:text-gray-400">
                Created{' '}
                {formatDistanceToNow(new Date(ticket.createdAt), { addSuffix: true })}
              </div>
            </div>

            {/* Conversation Thread */}
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Conversation</h2>

              {/* Original Message */}
              <div className="border-b border-gray-100 dark:border-gray-700 pb-6 mb-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="h-5 w-5 text-primary-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium text-gray-900 dark:text-white">You</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {format(new Date(ticket.createdAt), 'PPp')}
                      </span>
                    </div>
                    <div className="prose prose-sm max-w-none text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {ticket.description}
                    </div>
                  </div>
                </div>
              </div>

              {/* Messages — skip the first customer message if it matches the ticket description (already shown above as "Original Message") */}
              {(() => {
                const filteredMessages = (messages || []).filter((msg, index) => {
                  if (index === 0 && msg.senderType === 'customer' && msg.content === ticket.description) {
                    return false;
                  }
                  return true;
                });

                if (messagesLoading) {
                  return (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
                    </div>
                  );
                }

                if (filteredMessages.length === 0) {
                  return (
                    <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                      No replies yet. We'll get back to you soon.
                    </p>
                  );
                }

                return (
                <div className="space-y-6">
                  {filteredMessages.map((message) => {
                    const isCustomer = message.senderType === 'customer';
                    const isAgent = message.senderType === 'agent';
                    const isSystem = message.senderType === 'system';
                    const isHtmlContent = message.contentType === 'html' ||
                                        message.contentType === 'HTML' ||
                                        (message.content && message.content.includes('<p>'));
                    // Treat as bot if senderType is 'bot' OR if it's HTML content that looks like AI response
                    const isBot = message.senderType === 'bot' ||
                                  (isHtmlContent && message.content?.includes('knowledge base'));

                    // Special rendering for AI/Bot messages (including system messages with HTML that are actually AI responses)
                    if ((isBot || (isSystem && isHtmlContent)) && isHtmlContent) {
                      return (
                        <div key={message.id} className="mb-6">
                          {/* Bot Header */}
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                              <Bot className="h-5 w-5 text-white" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-purple-700">AI Assistant</span>
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 rounded-full">
                                  <Sparkles className="h-3 w-3 text-purple-600" />
                                  <span className="text-xs font-medium text-purple-700">Auto-Reply</span>
                                </span>
                              </div>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {format(new Date(message.createdAt), 'PPp')}
                              </span>
                            </div>
                          </div>

                          {/* AI Response Card */}
                          <div className="ml-13 pl-4 border-l-2 border-purple-200">
                            <AIResponseCard content={message.content} />
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div key={message.id} className="flex items-start gap-4">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isCustomer
                              ? 'bg-primary-100'
                              : isAgent
                              ? 'bg-blue-100'
                              : isBot
                              ? 'bg-purple-100'
                              : 'bg-gray-100 dark:bg-gray-800'
                          }`}
                        >
                          {isCustomer ? (
                            <User className="h-5 w-5 text-primary-600" />
                          ) : isAgent ? (
                            <Headphones className="h-5 w-5 text-blue-600" />
                          ) : isBot ? (
                            <Bot className="h-5 w-5 text-purple-600" />
                          ) : (
                            <AlertCircle className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-medium text-gray-900 dark:text-white">
                              {isCustomer
                                ? 'You'
                                : isAgent
                                ? message.senderName || 'Support Agent'
                                : isBot
                                ? 'AI Assistant'
                                : 'System'}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {format(new Date(message.createdAt), 'PPp')}
                            </span>
                          </div>
                          {isHtmlContent ? (
                            <div
                              className={`prose prose-sm max-w-none text-gray-700 dark:text-gray-300 [&>div]:bg-gray-50 dark:[&>div]:bg-gray-800 [&>div]:rounded-lg [&>div]:p-3 [&>div]:my-2 [&>div]:border [&>div]:border-gray-200 dark:[&>div]:border-gray-700 ${
                                isSystem ? 'text-gray-500 dark:text-gray-400 italic' : ''
                              }`}
                              // H-1: was raw `message.content`. Ticket messages originate from inbound
                              // email and agent replies, and this renders in the PUBLIC customer portal.
                              dangerouslySetInnerHTML={{ __html: sanitizeMessageHtml(message.content) }}
                            />
                          ) : (
                            <div
                              className={`prose prose-sm max-w-none whitespace-pre-wrap ${
                                isSystem ? 'text-gray-500 dark:text-gray-400 italic' : 'text-gray-700 dark:text-gray-300'
                              }`}
                            >
                              {message.content}
                            </div>
                          )}
                          {message.attachments && message.attachments.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {message.attachments.map((attachment, index) => (
                                <a
                                  key={index}
                                  href={attachment.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-200 transition-colors"
                                >
                                  <Paperclip className="h-4 w-4" />
                                  {attachment.filename}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                );
              })()}

              {/* Reply Form */}
              {isTicketOpen && (
                <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">Add a Reply</h3>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <textarea
                      {...register('content', {
                        required: 'Please enter a message',
                        minLength: {
                          value: 5,
                          message: 'Message must be at least 5 characters',
                        },
                      })}
                      rows={4}
                      placeholder="Type your message..."
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                    />
                    {errors.content && (
                      <p className="mt-1 text-sm text-red-600">{errors.content.message}</p>
                    )}

                    {replyMutation.isError && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-700">
                          Failed to send message. Please try again.
                        </p>
                      </div>
                    )}

                    <div className="mt-4 flex justify-end">
                      <button
                        type="submit"
                        disabled={replyMutation.isPending}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {replyMutation.isPending ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4" />
                            Send Reply
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {!isTicketOpen && (
                <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                    <p className="text-gray-600 dark:text-gray-400">
                      This ticket is {ticket.status}. If you need further assistance,
                      please{' '}
                      <Link
                        to="/submit-ticket"
                        className="text-primary-600 hover:text-primary-700 font-medium"
                      >
                        submit a new request
                      </Link>
                      .
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Ticket Details */}
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Details</h3>

              <dl className="space-y-4">
                <div>
                  <dt className="text-sm text-gray-500 dark:text-gray-400">Status</dt>
                  <dd className="mt-1">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${status.color}`}
                    >
                      <StatusIcon className="h-4 w-4" />
                      {status.label}
                    </span>
                  </dd>
                </div>

                <div>
                  <dt className="text-sm text-gray-500 dark:text-gray-400">Priority</dt>
                  <dd className="mt-1">
                    <span
                      className={`px-2 py-1 rounded-full text-sm font-medium ${priority.color}`}
                    >
                      {priority.label}
                    </span>
                  </dd>
                </div>

                <div>
                  <dt className="text-sm text-gray-500 dark:text-gray-400">Ticket Number</dt>
                  <dd className="mt-1 font-mono text-gray-900 dark:text-white">#{ticket.ticketNumber}</dd>
                </div>

                <div>
                  <dt className="text-sm text-gray-500 dark:text-gray-400">Created</dt>
                  <dd className="mt-1 text-gray-900 dark:text-white">
                    {format(new Date(ticket.createdAt), 'PPP')}
                  </dd>
                </div>

                {ticket.updatedAt !== ticket.createdAt && (
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Last Updated</dt>
                    <dd className="mt-1 text-gray-900 dark:text-white">
                      {format(new Date(ticket.updatedAt), 'PPP')}
                    </dd>
                  </div>
                )}

                {ticket.assignee && (
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Assigned To</dt>
                    <dd className="mt-1 text-gray-900 dark:text-white">{ticket.assignee.name}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Need More Help */}
            <div className="bg-primary-50 dark:bg-gray-800 rounded-xl p-6 mt-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Need immediate help?
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Browse our knowledge base for instant answers to common questions.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 rounded-lg text-sm font-medium hover:bg-primary-100 dark:hover:bg-gray-600 transition-colors border border-primary-200 dark:border-gray-600"
              >
                Browse Help Center
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
